/**
 * PostHog analytics — lazy-loaded to keep it off the critical render path.
 * EU cloud: eu.posthog.com
 *
 * POSTHOG_KEY is injected at build time via Vite's import.meta.env.
 * Set POSTHOG_KEY in Doppler project: syncupsuite-com
 *
 * Privacy: Uses localStorage persistence (not cookies). No cookie banner
 * required. Session recording is disabled. This is a deliberate choice —
 * we track page-level analytics without setting any cookies.
 */

import type { PostHog } from "posthog-js";

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as
  | string
  | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ??
  "https://eu.i.posthog.com";

// Singleton: resolves to the PostHog instance once loaded, or null if no key.
let posthogPromise: Promise<PostHog | null> | null = null;

function loadPostHog(): Promise<PostHog | null> {
  if (posthogPromise) return posthogPromise;

  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.info(
        "[analytics] PostHog not initialized — VITE_PUBLIC_POSTHOG_KEY not set",
      );
    }
    posthogPromise = Promise.resolve(null);
    return posthogPromise;
  }

  posthogPromise = import("posthog-js").then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY!, {
      api_host: POSTHOG_HOST,
      persistence: "localStorage",
      capture_pageview: true,
      disable_session_recording: true,
    });
    return posthog;
  });

  return posthogPromise;
}

/** Schedule PostHog load after paint — call from main.tsx. */
export function initAnalytics(): void {
  if (!POSTHOG_KEY) return;

  const schedule =
    typeof requestIdleCallback === "function"
      ? requestIdleCallback
      : (cb: () => void) => setTimeout(cb, 1500);

  schedule(() => loadPostHog());
}

// Fire-and-forget capture helper
function capture(event: string, properties?: Record<string, unknown>): void {
  loadPostHog().then((ph) => ph?.capture(event, properties));
}

// ---------- Typed event helpers ----------

export const analytics = {
  marketplaceInstallCTAClicked: () =>
    capture("marketplace_install_cta_clicked"),

  waitlistSignupSubmitted: (email?: string) =>
    capture("waitlist_signup_submitted", { email }),

  waitlistSignupCompleted: () => capture("waitlist_signup_completed"),

  docsCTAClicked: () => capture("docs_cta_clicked"),

  themePreviewViewed: (theme: string) =>
    capture("theme_preview_viewed", { theme }),
} as const;

// ---------- Feature flag helpers ----------
// Defaults are all `true` (features ON). Flags act as kill switches —
// the site works normally without PostHog or when flags are not configured.

export const flags = {
  isWaitlistOpen: async (): Promise<boolean> => {
    const ph = await loadPostHog();
    return ph?.isFeatureEnabled("syncupsuite-com-waitlist-open") ?? true;
  },

  isDocsLinkVisible: async (): Promise<boolean> => {
    const ph = await loadPostHog();
    return ph?.isFeatureEnabled("docs-link-visible") ?? true;
  },

  isThemeGalleryEnabled: async (): Promise<boolean> => {
    const ph = await loadPostHog();
    return ph?.isFeatureEnabled("theme-gallery-enabled") ?? true;
  },
} as const;
